const CACHE_TTL_MS = 60 * 1000; // 1 minute
const SCOPE = 'profile';

let cachedToken: string | null = null;
let cacheExpiry = 0;

export interface ScopedTokenResponse {
  token: string;
}

/**
 * Fetches a scoped token from the API for cross-domain requests (e.g. traits).
 * Uses credentials: 'include' so cookies are sent to the API domain.
 * Caches the token for 1 minute to avoid excessive API calls.
 */
export async function getScopedToken(): Promise<string | null> {
  if (cachedToken && Date.now() < cacheExpiry) {
    return cachedToken;
  }

  const apiHost = process.env.NEXT_PUBLIC_API_HOST;
  if (!apiHost || globalThis.window === undefined) {
    return null;
  }

  const url = `${ apiHost }/2.0/auth/token/${ SCOPE }`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      return null;
    }

    const data = (await response.json()) as ScopedTokenResponse;
    const token = data?.token;

    if (typeof token === 'string' && token.length > 0) {
      cachedToken = token;
      cacheExpiry = Date.now() + CACHE_TTL_MS;
      return token;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Clears the scoped token cache. Call on logout or when auth state is cleared.
 */
export function clearScopedTokenCache(): void {
  cachedToken = null;
  cacheExpiry = 0;
}
